import { useEffect, useState } from 'react';
import { preOrderService } from '../../services/preOrderService';
import type { PreOrder, PreOrderStatus } from '../../types/catalog';

interface UsePreOrderDataParams {
  companyId: number | null;
}

const ALL_STATUSES: PreOrderStatus[] = ['NEW', 'CONFIRMED', 'FULFILLED', 'CANCELLED'];

export function usePreOrderData({ companyId }: UsePreOrderDataParams) {
  const [preOrders, setPreOrders] = useState<PreOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  // Filters: by default only active (NEW/CONFIRMED) pre-orders, oldest first
  const [showOnlyActive, setShowOnlyActive] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      if (!companyId) {
        setError('Ідентифікатор компанії відсутній');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await preOrderService.search(companyId, {
          status: showOnlyActive ? undefined : ALL_STATUSES,
          page: page - 1,
          size: 10,
        });
        if (cancelled) {
          return;
        }
        setPreOrders(response.content);
        setTotalPages(response.totalPages);
        setError(null);
      } catch (err) {
        console.error('Error fetching pre-orders:', err);
        if (!cancelled) {
          setError('Не вдалося завантажити передзамовлення');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setIsFirstLoad(false);
        }
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [companyId, page, showOnlyActive]);

  const handleShowOnlyActiveChange = (value: boolean) => {
    setShowOnlyActive(value);
    setPage(1);
  };

  return {
    data: {
      preOrders,
      loading,
      isFirstLoad,
      error,
    },
    pagination: {
      count: totalPages,
      page,
      onChange: (_: React.ChangeEvent<unknown>, value: number) => setPage(value),
    },
    filters: {
      showOnlyActive,
      setShowOnlyActive: handleShowOnlyActiveChange,
    },
  };
}
